import React, { useEffect, useMemo, useState } from "react";
import {
  IconButton,
  Box,
  Stack,
  InputBase,
  Typography,
  List,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { TreeView } from "@mui/x-tree-view/TreeView";
import {
  TreeItem,
  TreeItemProps,
  treeItemClasses,
} from "@mui/x-tree-view/TreeItem";
import { Close, FilterList, Add, Remove } from "@mui/icons-material";
import { SvgIconProps } from "@mui/material/SvgIcon";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import style from "./component.module.scss";
import useMenuStore from "../store/menu";
import usePermissionStore from "../store/permission";
import { hasRequiredPermission } from "./PermissionGuard";

interface SideMenuConfigItem {
  id: string;
  translationKey: string;
  path: string;
  permission?: string | string[];
}

export const SIDE_MENU_CONFIG: SideMenuConfigItem[] = [
  {
    id: "event-library",
    translationKey: "menu.eventLibrary",
    path: "/event-library",
    permission: "operate_event_library",
  },
  // {
  //   id: "timeline-template",
  //   translationKey: "menu.timelineTemplate",
  //   path: "/timeline-template",
  //   permission: "operate_timeline_template",
  // },
];

interface CustomMenuItem {
  id: string;
  displayName: string;
  translationKey: string;
  path: string;
  disabled: boolean;
  permission?: string | string[];
}

interface SideMenuProps {
  onChange?: (node: CustomMenuItem, pageType?: string) => void;
  menuData?: CustomMenuItem[];
  isEdit?: boolean | undefined;
}

type StyledTreeItemProps = TreeItemProps & {
  bgColor?: string;
  bgColorForDarkMode?: string;
  color?: string;
  colorForDarkMode?: string;
  labelIcon?: React.ElementType<SvgIconProps>;
  labelInfo?: string;
  labelText: string;
  nodeInfo: CustomMenuItem;
};

const StyledTreeItemRoot = (styled(TreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 0,
    [`& .${treeItemClasses.content}`]: {
      paddingLeft: theme.spacing(0),
    },
  },
})) as unknown) as typeof TreeItem;

const SideMenu = (props: SideMenuProps) => {
  const {
    isEdit,
  } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [keywords, setKeywords] = useState<string>("");
  const [selected, setSelected] = useState<string>("");
  const [expanded, setExpanded] = useState<string[]>([]);
  const [menuVisible] = useMenuStore((state) => [state.menuVisible]);
  const permissions = usePermissionStore((state) => state.permissions);

  useEffect(() => {
    const lastSegment = location.pathname.split("/").filter(Boolean).pop();
    if (lastSegment) {
      setSelected(lastSegment);
    } else {
      setSelected("");
    }
  }, [location.pathname]);

  const keywordsFilterMenuResult = useMemo(() => {
      const allowedMenuData = SIDE_MENU_CONFIG.filter((item) =>
        hasRequiredPermission(permissions, item.permission)
      );
    const translatedMenuData = allowedMenuData.map((item) => ({
      id: item.id,
      translationKey: item.translationKey,
      path: item.path,
        disabled: false,
      permission: item.permission,
      displayName: t(item.translationKey),
    }));
    if (!keywords) {
      return translatedMenuData;
    }
    return translatedMenuData.filter((item) =>
      item.displayName?.toLowerCase().includes(keywords.toLowerCase())
    );
  }, [keywords, permissions, t]);

  const trimmedKeywords = keywords.trim();
  const showNoSearchResults =
    trimmedKeywords.length > 0 && keywordsFilterMenuResult.length === 0;

  const StyledTreeItem = React.forwardRef(function StyledTreeItem(
    props: StyledTreeItemProps,
    ref: React.Ref<HTMLLIElement>
  ) {
    const {
      bgColor,
      color,
      labelIcon: LabelIcon,
      labelInfo,
      labelText,
      colorForDarkMode,
      bgColorForDarkMode,
      nodeInfo,
      ...other
    } = props;

    return (
      <StyledTreeItemRoot
        onClick={() => {
          if (nodeInfo.disabled) return;
          setSelected(nodeInfo.id);
          navigate(nodeInfo.path);
        }}
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pr: 0,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: "500", flexGrow: 1 }}>
              {labelText}
            </Typography>
            {LabelIcon && <Box component={LabelIcon} color="inherit" sx={{ mr: 1 }} />}
          </Box>
        }
        {...other}
        ref={ref}
      />
    );
  });

  const renderTree = (nodes: CustomMenuItem, isEdit: boolean | undefined) => (
    <StyledTreeItem
      nodeId={nodes.id}
      labelText={nodes.displayName}
      nodeInfo={nodes}
      disabled={nodes.disabled ?? false}
      key={nodes.id}
    />
  );

  return (
    <Box
      className={style["side-menu-part"]}
      display={menuVisible ? "block" : "none"}
    >
      <Stack
        direction="row"
        className={style["side-menu-searchbar"]}
      >
        <IconButton type="button">
          {!keywords && <FilterList color="inherit"></FilterList>}
        </IconButton>
        <InputBase
          placeholder={t("common.search")}
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
        />
        {!!keywords && (
          <IconButton type="button">
            <Close
              onClick={() => {
                setKeywords("");
              }}
            />
          </IconButton>
        )}
      </Stack>
      <Box className="sidenavigation-component">
        <List
          className="rightsidesearch-list"
          component="nav"
          aria-labelledby="nested-list-subheader"
        >
          {showNoSearchResults ? (
            <Box sx={{ px: 1.5, py: 2 }}>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {t("common.noSearchResults")}
              </Typography>
            </Box>
          ) : (
            <TreeView
              disabledItemsFocusable={true}
              selected={selected ? [selected] : []}
              expanded={expanded}
              defaultExpandIcon={<Add />}
              defaultCollapseIcon={<Remove />}
              defaultEndIcon={<div style={{ width: 24 }} />}
              onNodeToggle={(e: React.SyntheticEvent, nodeIds: string[]) => {
                setExpanded(nodeIds);
              }}
              sx={{
                height: "100%",
                flexGrow: 1,
                maxWidth: 400,
                overflowY: "auto",
              }}
            >
              {keywordsFilterMenuResult.map((item) => {
                return renderTree(item, isEdit);
              })}
            </TreeView>
          )}
        </List>
      </Box>
    </Box>
  );
};

export default SideMenu;
